import React, { useEffect, useRef, useState } from 'react';
import { ConfigProvider, InputNumber } from 'antd';
import { InputNumberProps } from 'antd/lib';
import classNames from 'classnames';
import { CloseCircleFilled } from '@ant-design/icons';
import styles from './NumericInput.module.css';

export type NumericInputProps = InputNumberProps<number> & {
	asAsync?: boolean;
	allowNegative?: boolean;
	separateThousands?: boolean;
	min?: number;
	isRequiredHighlight?: boolean;
	isRequiredHighlightBorder?: boolean;
	fullWidth?: boolean;
	highlightOnNull?: boolean;
	allowClear?: boolean;
	onClear?: () => void;
	'data-testid'?: string;
};

const NumericInput = ({
	allowNegative = true,
	separateThousands = true,
	value: incomingValue,
	asAsync,
	isRequiredHighlight,
	isRequiredHighlightBorder,
	highlightOnNull = false,
	fullWidth = true,
	allowClear = false,
	onClear,
	...props
}: NumericInputProps) => {
	const [localValue, setLocalValue] = useState<number | string | null | undefined>(incomingValue);
	const decimalSeparator = (localStorage.getItem('decimalSeparator') as '.' | ',') || '.';
	const thousandSeparator = decimalSeparator === ',' ? '.' : ',';
	const isEditingRef = useRef(false);
	const { componentDisabled } = ConfigProvider.useConfig();
	const disabled = props.disabled || componentDisabled;

	// Sync from parent unlessss user is typing
	useEffect(() => {
		if (asAsync == null) {
			return;
		}

		if (isEditingRef.current) {
			return;
		}

		setLocalValue(incomingValue);
	}, [incomingValue, asAsync]);

	const formatter = (value?: string | number) => {
		if (value == null || value === '') {
			return '';
		}

		const str = value.toString();
		const [int, dec] = str.split('.');
		const intWithSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

		return dec != null ?
			`${intWithSep}${decimalSeparator}${dec}` :
			intWithSep;
	};

	const parser = (displayValue: string = ''): number => {
		let value = displayValue;

		if (!allowNegative && value[0] === '-') {
			value = value.replace('-', '');	
		}

		const reThou = new RegExp(`\\${thousandSeparator}`, 'g');
		const normalized = value.replace(reThou, '').replace(decimalSeparator, '.');

		return parseFloat(normalized);
	};

	const handleChange = async (value?: number | null) => {
		if (value === null || value === undefined || Number.isNaN(+value)) {
			onChange?.(null);
		} else {
			// if it’s a string, parse it; if it’s already a number, just forward
			const parsed = typeof value === 'string' ? parser(value) : value;
			setLocalValue(parsed);
			onChange?.(parsed as number);
		}
	};

	const handleInput = (value: string) => {
		if (value === '') {
			onChange?.(null);
			setLocalValue(null);

			return;
		}

		const parsed = typeof value === 'string' ? parser(value) : value;

		if (Number.isNaN(parsed)) {
			return;
		}

		setLocalValue(parsed);
	};

	const clear = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		isEditingRef.current = false;
		setLocalValue(null);
		onChange?.(null);
		onClear?.();
	};

	const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
		isEditingRef.current = true;
		props.onFocus?.(e);
	};

	const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
		isEditingRef.current = false;

		// Re-sync to the parent's canonical value once the user stops typing so the
		// display reflects any normalization the parent applied.
		if (asAsync != null) {
			setLocalValue(incomingValue);
		}

		props.onBlur?.(e);
	};

	const { onChange, suffix } = props;

	const currentValue = asAsync ? localValue : incomingValue;

	const canClear =
		allowClear &&
		!disabled &&
		!props.readOnly &&
		currentValue !== null &&
		currentValue !== undefined &&
		currentValue !== '';

	const suffixWithClear = allowClear ? (
		<span className={styles.suffixWrap}>
			{canClear && (
				<CloseCircleFilled
					className={styles.clearIcon}
					onMouseDown={(e) => e.preventDefault()}
					onClick={clear}
				/>
			)}
			{suffix}
		</span>
	) : suffix;

	let highlightClass;

	if (isRequiredHighlight) {
		highlightClass = styles.highlightRequired;
	} else if (isRequiredHighlightBorder) {
		highlightClass = styles.highlightRequiredBorder;
	}

	const value = asAsync ? (localValue as number) : incomingValue;

	if (highlightOnNull && (value == null || value === 0)) {
		highlightClass = styles.highlightRequiredBorder;
	}

	return (
		<div className={highlightClass}>
			<InputNumber<number>
				decimalSeparator={decimalSeparator}
				controls={false}
				formatter={!separateThousands ? undefined : formatter}
				parser={!separateThousands ? undefined : parser}
				min={allowNegative ? undefined : 0}
				{...props}
				disabled={disabled}
				value={value}
				onChange={handleChange}
				onInput={handleInput}
				onFocus={handleFocus}
				onBlur={handleBlur}
				aria-invalid={isRequiredHighlight ? true : undefined}
				className={classNames(fullWidth ? styles.inputFullWidth : styles.input, props.className)}
				suffix={suffixWithClear}
			/>
		</div>
	);
};

export default NumericInput;
